import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  Box, Typography, Button, Chip, CircularProgress, ToggleButton, ToggleButtonGroup,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, LinearProgress, Tooltip,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import StarIcon from "@mui/icons-material/Star";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import RefreshIcon from "@mui/icons-material/Refresh";
import { toast } from "react-toastify";
import { getPlans, getSubscription, getEntitlements, startCheckout, openBillingPortal } from "./api/billingApi";

const INK = "#0B2A4A", BLUE = "#1E6FE8", GREEN = "#12A150", MUTED = "#64748b", LINE = "#e3ebf3";

const money = (n, cur = "usd") =>
  n == null ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: (cur || "usd").toUpperCase(), maximumFractionDigits: 0 }).format(n);

const STATUS_STYLE = {
  active: { label: "Active", c: "#15803d", bg: "#dcfce7" },
  trialing: { label: "Trial", c: "#1d4ed8", bg: "#dbeafe" },
  past_due: { label: "Past due", c: "#b45309", bg: "#fef3c7" },
  canceled: { label: "Canceled", c: "#b91c1c", bg: "#fee2e2" },
  none: { label: "No plan", c: "#475569", bg: "#f1f5f9" },
};

export default function BillingPage() {
  // When an admin views a tenant via /tenant-dashboard/:tenantIdp/billing, use
  // the tenant from the URL; otherwise fall back to the logged-in user's tenant.
  const { tenantIdp } = useParams();
  const reduxTenantId = useSelector((s) => s.userInfo.tenantId);
  const tenantId = tenantIdp || reduxTenantId;
  const isAdmin = useSelector((s) => s.userInfo.isAdmin);
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  const [interval, setInterval] = useState("month");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyKey, setBusyKey] = useState(null);
  const [notice, setNotice] = useState(null); // { title, body }

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      // The plan catalog always loads. Subscription/entitlements need a tenant.
      const pRes = await getPlans();
      setPlans((pRes?.data || []).filter((p) => p.active !== false));
      if (tenantId) {
        const [sRes, eRes] = await Promise.all([
          getSubscription(tenantId).catch(() => ({ data: null })),
          getEntitlements(tenantId).catch(() => ({ data: null })),
        ]);
        setSubscription(sRes?.data || null);
        setEntitlements(eRes?.data || null);
      } else {
        setSubscription(null);
        setEntitlements(null);
      }
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Failed to load billing information");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const currentPlanKey = subscription?.planKey || (entitlements?.active ? entitlements?.planKey : null);
  const status = subscription?.status || "none";
  const st = STATUS_STYLE[status] || STATUS_STYLE.none;

  const handleChoose = async (plan) => {
    if (plan.pricing?.isCustomPriced) {
      setNotice({ title: "Let's talk", body: `${plan.name} is tailored to your needs. Your CrediArc account manager will set you up with custom limits and pricing.` });
      return;
    }
    setBusyKey(plan.key);
    try {
      const res = await startCheckout({ planKey: plan.key, interval, tenantId });
      if (res?.url) { window.location.href = res.url; return; }
      setNotice({ title: "Almost there", body: "Checkout could not be started. Please try again or contact support." });
    } catch (e) {
      if (e?.response?.status === 503) {
        setNotice({ title: "Payments aren't enabled yet", body: "Online checkout is being set up. In the meantime, contact your CrediArc account manager to change your plan." });
      } else {
        toast.error(e?.response?.data?.error || e?.message || "Could not start checkout");
      }
    } finally {
      setBusyKey(null);
    }
  };

  const handlePortal = async () => {
    try {
      const res = await openBillingPortal({ tenantId });
      if (res?.url) { window.location.href = res.url; return; }
    } catch (e) {
      if (e?.response?.status === 503 || e?.response?.status === 404) {
        setNotice({ title: "Billing portal not available yet", body: "Self-serve billing management will be available once payments are enabled." });
      } else {
        toast.error(e?.response?.data?.error || e?.message || "Could not open billing portal");
      }
    }
  };

  const priceFor = (plan) => (interval === "year" ? plan.pricing?.annual : plan.pricing?.monthly);
  const annualSavingPct = (plan) => {
    const m = plan.pricing?.monthly, a = plan.pricing?.annual;
    if (!m || !a) return null;
    const pct = Math.round((1 - a / (m * 12)) * 100);
    return pct > 0 ? pct : null;
  };

  const usage = useMemo(() => {
    const quota = entitlements?.counterpartiesPerMonth;
    const used = subscription?.usageThisPeriod ?? null;
    if (quota == null || used == null) return null;
    return { used, quota, pct: Math.min(100, Math.round((used / quota) * 100)) };
  }, [entitlements, subscription]);

  // Admin-only for now (until GA). Blocks direct navigation to /billing too.
  if (!isAdmin) {
    return (
      <Box sx={{ maxWidth: 640, mx: "auto", px: 4, py: 8, textAlign: "center" }}>
        <Typography sx={{ fontSize: 20, fontWeight: 700, color: INK, mb: 1 }}>Billing &amp; Plans</Typography>
        <Typography sx={{ fontSize: 14.5, color: MUTED }}>This section isn’t available yet.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1180, mx: "auto", px: { xs: 2, md: 4 }, py: 3 }}>
      {/* header */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: 26, fontWeight: 800, color: INK, letterSpacing: "-.01em" }}>Billing &amp; Plans</Typography>
          <Typography sx={{ fontSize: 14, color: MUTED }}>Manage your CrediArc subscription and see what each plan includes.</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button startIcon={<RefreshIcon />} onClick={load} sx={{ textTransform: "none", color: INK }}>Refresh</Button>
          <Button startIcon={<CreditCardIcon />} variant="outlined" onClick={handlePortal} sx={{ textTransform: "none", borderColor: LINE, color: INK }}>Manage billing</Button>
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 8, gap: 1.5 }}>
          <CircularProgress size={24} /><Typography sx={{ color: MUTED }}>Loading…</Typography>
        </Box>
      )}

      {!loading && error && <Alert severity="warning" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>{error}</Alert>}

      {!loading && !error && (
        <>
          {/* current plan summary */}
          <Box sx={{ border: `1px solid ${LINE}`, borderRadius: "14px", p: 2.5, mb: 3, background: "linear-gradient(180deg,#f7fafe,#ffffff)" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: MUTED }}>Current plan</Typography>
              <Chip size="small" label={st.label} sx={{ height: 20, fontSize: 11, fontWeight: 700, color: st.c, background: st.bg }} />
            </Box>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, mt: 0.5 }}>
              <Typography sx={{ fontSize: 24, fontWeight: 800, color: INK }}>
                {currentPlanKey ? (plans.find((p) => p.key === currentPlanKey)?.name || currentPlanKey) : "No active plan"}
              </Typography>
              {subscription?.currentPeriodEnd && (
                <Typography sx={{ fontSize: 13, color: MUTED }}>
                  {subscription.cancelAtPeriodEnd ? "Ends" : "Renews"} {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                </Typography>
              )}
            </Box>
            {usage && (
              <Box sx={{ mt: 1.5, maxWidth: 460 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                  <Typography sx={{ fontSize: 12.5, color: MUTED }}>Counterparties this period</Typography>
                  <Typography sx={{ fontSize: 12.5, color: INK, fontWeight: 600 }}>{usage.used} / {usage.quota}</Typography>
                </Box>
                <LinearProgress variant="determinate" value={usage.pct} sx={{ height: 7, borderRadius: 4, backgroundColor: "#eef2f7", "& .MuiLinearProgress-bar": { backgroundColor: usage.pct > 85 ? "#b45309" : BLUE } }} />
              </Box>
            )}
            {!currentPlanKey && <Typography sx={{ fontSize: 13.5, color: MUTED, mt: 0.5 }}>Choose a plan below to get started.</Typography>}
          </Box>

          {/* billing interval toggle */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2.5 }}>
            <ToggleButtonGroup exclusive size="small" value={interval} onChange={(_, v) => v && setInterval(v)}
              sx={{ background: "#f1f5f9", borderRadius: "999px", p: 0.5, "& .MuiToggleButton-root": { border: "none", borderRadius: "999px !important", textTransform: "none", px: 2.5, py: 0.5, fontWeight: 600, color: MUTED }, "& .Mui-selected": { background: "#fff !important", color: `${INK} !important`, boxShadow: "0 1px 3px rgba(11,42,74,.12)" } }}>
              <ToggleButton value="month">Monthly</ToggleButton>
              <ToggleButton value="year">Annual <Chip size="small" label="save" sx={{ ml: 0.75, height: 16, fontSize: 9.5, fontWeight: 700, color: GREEN, background: "#e7f6ed" }} /></ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* tiers */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: `repeat(${Math.min(4, plans.length) || 1}, 1fr)` }, gap: 2 }}>
            {plans.map((plan) => {
              const isCurrent = plan.key === currentPlanKey;
              const price = priceFor(plan);
              const saving = interval === "year" && annualSavingPct(plan);
              return (
                <Box key={plan.key} sx={{ position: "relative", border: plan.popular ? `1.5px solid ${BLUE}` : `1px solid ${LINE}`, borderRadius: "16px", p: 2.5, background: "#fff", boxShadow: plan.popular ? "0 12px 32px rgba(11,42,74,.10)" : "0 1px 2px rgba(11,42,74,.05)", display: "flex", flexDirection: "column" }}>
                  {plan.popular && <Chip icon={<StarIcon sx={{ fontSize: "14px !important", color: "#fff !important" }} />} label="Most popular" size="small" sx={{ position: "absolute", top: -11, left: 20, height: 22, fontSize: 10.5, fontWeight: 700, color: "#fff", background: BLUE }} />}
                  <Typography sx={{ fontSize: 17, fontWeight: 800, color: INK }}>{plan.name}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: MUTED, minHeight: 34, mt: 0.25 }}>{plan.audience}</Typography>
                  <Box sx={{ mt: 1.5, mb: 1.5 }}>
                    {plan.pricing?.isCustomPriced ? (
                      <Typography sx={{ fontSize: 26, fontWeight: 800, color: INK }}>Custom</Typography>
                    ) : (
                      <>
                        <Typography component="span" sx={{ fontSize: 30, fontWeight: 800, color: INK }}>{money(price, plan.pricing?.currency)}</Typography>
                        <Typography component="span" sx={{ fontSize: 13, color: MUTED, fontWeight: 600 }}>/{interval === "year" ? "yr" : "mo"}</Typography>
                        {saving ? <Typography sx={{ fontSize: 11.5, color: GREEN, fontWeight: 700 }}>Save {saving}% vs monthly</Typography> : <Box sx={{ height: 17 }} />}
                      </>
                    )}
                  </Box>
                  <Button
                    onClick={() => handleChoose(plan)}
                    disabled={isCurrent || busyKey === plan.key}
                    variant={plan.popular ? "contained" : "outlined"}
                    fullWidth
                    sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", mb: 2,
                      ...(plan.popular ? { background: BLUE } : { borderColor: LINE, color: INK }),
                      ...(isCurrent ? { background: "#eef2f7", color: MUTED } : {}) }}
                  >
                    {busyKey === plan.key ? "Starting…" : isCurrent ? "Current plan" : plan.pricing?.isCustomPriced ? "Contact sales" : "Choose " + plan.name}
                  </Button>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {(plan.featureList || []).map((f, i) => (
                      <Box key={i} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                        <CheckIcon sx={{ fontSize: 17, color: GREEN, mt: "1px", flexShrink: 0 }} />
                        <Typography sx={{ fontSize: 13, color: "#334155" }}>{f}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Box>

          {plans.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>No plans are available yet. An administrator can define them in Admin → Plans &amp; Packaging.</Alert>
          )}

          <Typography sx={{ fontSize: 12, color: "#94a3b8", textAlign: "center", mt: 3 }}>
            Prices shown in the plan currency. Taxes calculated at checkout. Overage on counterparties billed per your plan.
          </Typography>
        </>
      )}

      {/* graceful notices */}
      <Dialog open={!!notice} onClose={() => setNotice(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{notice?.title}</DialogTitle>
        <DialogContent><Typography sx={{ fontSize: 14.5, color: "#334155" }}>{notice?.body}</Typography></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}><Button variant="contained" onClick={() => setNotice(null)} sx={{ textTransform: "none", background: BLUE }}>Got it</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
